import IconButton from '@material-ui/core/IconButton';
import PrimaryButton from '../PrimaryButton';
import InAppBar from '../../core/InAppBar';
import withWidth from '@material-ui/core/withWidth';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import PropTypes from 'prop-types';
import { Component, default as React } from 'react';
import KebabMenu from '../../core/KebabMenu';
import GS from '../../grafsync/grafsync';
import Errored from '../Errored';
import Loading from '../Loading';
import AccessibleTooltip from '../AccessibleTooltip';
import { globalStyles as styles } from '../style/gstheme';
import TitledPanel from '../TitledPanel';
import { makeCancelable } from '../utils/utils';
import UpdateSubscriptionDialog from './UpdateSubscriptionDialog';
import TransferSubscriptionDialog from './TransferSubscriptionDialog';
import PortSubscriptionDialog from './PortSubscriptionDialog';
import SubscriptionsIcon from '@material-ui/icons/Subscriptions';
import StorageIcon from '@material-ui/icons/Storage';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import DetailedStatisticsDialog from './DetailedStatisticsDialog';

const tabItems = [{
  icon: SubscriptionsIcon,
  label: "Subscriptions",
},
{
  icon: StorageIcon,
  label: "System"
}];


class SystemStatistics extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      tabValue: 0,
      userCount: 0,
      bucketAllocations: {},
      subscriptionCount: 0,
      subscriptionToUpdate: undefined,
      subscriptionToTransfer: undefined,
      subscriptionToPort: undefined,
      detailedStatistics: false
    };
    this.cancellable = null;
  }

  changeTab = (tabValue) => this.setState({ tabValue });

  componentDidMount() {
    this.getSystemStatistics(this.props.user);
  }

  getSystemStatistics() {
    this.setState(
      { isLoading: true },
      async () => {
        try {
          const statistics = await this.doGetStatistics().promise;
          const { activity, systemStatistics, tripleStatistics, subscriptionStatistics, subscriptionTripleStatistics } = statistics;
          const bucketAllocations = {};
          subscriptionTripleStatistics.forEach(sts => {
            const bucket = sts.bucket;
            if (!bucketAllocations[bucket]) bucketAllocations[bucket] = 0;
            let bucketPoints = 1;
            if (sts.subscriptionType === "Enterprise" || sts.subscriptionType === "Premium") bucketPoints = 28;
            if (sts.subscriptionType === "Standard") bucketPoints = 7;
            bucketAllocations[bucket] = bucketAllocations[bucket] + bucketPoints;
          });
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.setState({
            statisticsTime: Date.now(),
            activity,
            tripleStatistics,
            systemStatistics,
            subscriptionStatistics,
            subscriptionTripleStatistics,
            bucketAllocations,
            isLoading: false
          });
        }
        catch (e) {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
      });
  }

  doGetStatistics() {
    this.cancellable = makeCancelable(GS.getSystemStatistics());
    return this.cancellable;
  }

  doUpdateSubscription(subscriptionIRI, payload) {
    this.cancellable = makeCancelable(GS.updateSubscription(subscriptionIRI, payload));
    return this.cancellable;
  }

  doTransferSubscription(payload) {
    this.cancellable = makeCancelable(GS.transferSubscription(payload));
    return this.cancellable;
  }

  doPortSubscription(payload) {
    this.cancellable = makeCancelable(GS.portSubscription(payload));
    return this.cancellable;
  }

  componentWillUnmount() {
    clearTimeout(this.idleTimeout);
    if (this.cancellable) this.cancellable.cancel();
  }

  renewSubscription = (subscription) => {
    GS.renewSubscription(subscription);
    this.refreshScreen();
  };

  optimiseSubscription = (subscription) => {
    GS.optimiseSubscription(subscription);
  };

  addExpirationDate = (subscription) => {
    // Set a week ahead
    const expirationDate = Date.now() + 86400000 * 7;
    this.setState({ subscriptionToUpdate: subscription }, () => {
      this.updateSubscription({ expirationDate });
    });
  };

  blockSubscription = (subscription) => {
    GS.blockSubscription(subscription);
    this.refreshScreen();
  };

  unblockSubscription = (subscription) => {
    GS.unblockSubscription(subscription);
    this.refreshScreen();
  };

  setSubscriptionToUpdate = (subscription) => this.setState({ subscriptionToUpdate: subscription });

  setSubscriptionToTransfer = (subscription) => this.setState({ subscriptionToTransfer: subscription });

  refreshScreen = () => {
    this.setState({ isLoading: true });
    this.idleTimeout = setTimeout(async () => {
      this.getSystemStatistics(this.props.user);
    }, 2000);
  };

  portSubscription = async (data) => {
    try {
      await this.doPortSubscription({
        subscription: data.subscription,
        subscriptionToPort: data.subscriptionToPort
      }).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ subscriptionToPort: undefined });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  getDiskspace = async () => {
    GS.diskspaceCheck();
    this.refreshScreen();
  };

  updateStatistics = async () => {
    GS.updateStatistics();
    this.refreshScreen();
  };

  setSubscriptionToPort = (subscription) => this.setState({ subscriptionToPort: subscription });

  updateSubscription = async (data) => {
    try {
      const { maxUsers, maxTriples, maxImportSize, queryTimeout, downloadTimeout, expirationDate, subscriptionVersion, module } = data;
      const { subscriptionToUpdate } = this.state;
      await this.doUpdateSubscription(subscriptionToUpdate, {
        maxUsers,
        maxTriples,
        maxImportSize,
        queryTimeout,
        downloadTimeout,
        expirationDate,
        subscriptionVersion,
        module
      }).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ subscriptionToUpdate: undefined });
      this.refreshScreen();
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  transferSubscription = async (data) => {
    try {
      await this.doTransferSubscription({
        subscription: data.subscription,
        username: data.username
      }).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ subscriptionToTransfer: undefined });
      this.refreshScreen();
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  cancelTransferSubscription = () => this.setState({ subscriptionToTransfer: undefined });

  cancelUpdateSubscription = () => this.setState({ subscriptionToUpdate: undefined });

  cancelPortSubscription = () => this.setState({ subscriptionToPort: undefined });

  detailedView = (subscription) => this.setState({ detailedStatistics: subscription });

  cancelDetailedView = () => this.setState({ detailedStatistics: undefined });

  getTableRow(row, rowIndex, classes, highlight, options, testId, subName) {
    return <TableRow
      data-test={testId || "row-" + rowIndex}
      key={"row-" + rowIndex}
      style={highlight ? { backgroundColor: highlight } : {}}>
      {row.map((cell, cellIndex) =>
        <TableCell
          data-test={"cell-" + cellIndex}
          key={cellIndex}
          style={{ verticalAlign: 'middle', height: 30, maxWidth: 250 }}
          className={classes.tableCell}>
          <Typography className={classes.tableCellText} style={highlight ? { color: 'white' } : {}}>{cell}</Typography>
        </TableCell>
      )}
      <TableCell align="center" data-test="sub-options" className={classes.tableCell} style={{ verticalAlign: 'middle' }}>
        <div data-test={"options-" + subName}>
          {options && options.length > 0 ?
            <KebabMenu
              dataTest={"menu-" + testId}
              items={options.map(option => option.label)}
              onSelect={(index) => options[index].action(options[index].subscription)}
            />
            :
            <Typography variant="body2" style={{ color: 'white' }}>DEL</Typography>
          }
        </div>
      </TableCell>
    </TableRow>;
  }

  render() {

    const { classes, width, user } = this.props;
    const { isLoading, isErrored, systemStatistics, tripleStatistics, subscriptionToTransfer, subscriptionToUpdate, subscriptionToPort,
      activity, tabValue, subscriptionStatistics, subscriptionTripleStatistics, statisticsTime, bucketAllocations, statistic, detailedStatistics } = this.state;
    const subscriptionOptions = GS.subscriptionOptions();
    const currentDate = Date.now();

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading) return <Loading />;

    const appBar = <InAppBar
      tabValue={tabValue}
      onChangeTab={this.changeTab}
      tabItems={tabItems} />;

    return (
      <div style={{ paddingBottom: 30 }}>
        <Typography className={classes.subMainHeading}>As at {new Date(statisticsTime).toLocaleString()}</Typography>
        <div>
          {appBar}
        </div>
        <div style={{ marginRight: 30, backgroundColor: 'white', borderRadius: 4, padding: 12 }}>
          {tabValue === 0 &&
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TitledPanel height="full" dataTest="basic-statistics" fontSize="large" title="Basic Statistics">
                  <Typography className={classes.subSubHeading}>User Count</Typography>
                  <Typography data-test="user-count" className={classes.statistic}>{systemStatistics.userCount}</Typography>
                  <Typography className={classes.subSubHeading}>Subscription Count</Typography>
                  <Typography data-test="subscription-count" className={classes.statistic}>{systemStatistics.subscriptionCount}</Typography>
                </TitledPanel>
              </Grid>
              <Grid item xs={12} md={3}>
                <TitledPanel dataTest="bucket-statistics" height="full" fontSize="large" title="Bucket Statistics">
                  <Table>
                    <TableHead style={{ backgroundColor: '#e8e8e8' }}>
                      <TableRow>
                        {
                          ["Bucket", "Subscriptions", "Points", "Triples"].map((col, index) =>
                            <TableCell key={index} component="th" className={classes.tableHeading}>{col}</TableCell>
                          )
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ backgroundColor: 'white' }}>
                      {tripleStatistics.sort((a, b) => a.bucket < b.bucket ? -1 : 1).map((item, rowIndex) => {
                        const row = [
                          item.bucket,
                          item.bucketSubscriptionCount,
                          bucketAllocations[item.bucket],
                          item.bucketTripleCount
                        ];
                        return (
                          <TableRow
                            key={"row-" + rowIndex}>
                            {row.map((cell, cellIndex) =>
                              <TableCell
                                data-test={"cell-" + cellIndex}
                                key={cellIndex}
                                className={classes.tableCell}>
                                <Typography className={classes.tableCellText}>{cell}</Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }
                      )}
                    </TableBody>
                  </Table>
                </TitledPanel>
              </Grid>
              <Grid item xs={12} md={3}>
                <TitledPanel height="full" dataTest="subscription-statistics" fontSize="large" title="Subscription Statistics">
                  <Table>
                    <TableHead style={{ backgroundColor: '#e8e8e8' }}>
                      <TableRow>
                        {
                          ["Type", "Count"].map((col, index) =>
                            <TableCell key={index} component="th" className={classes.tableHeading}>{col}</TableCell>
                          )
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ backgroundColor: 'white' }}>
                      {subscriptionStatistics
                        .sort((a, b) => {
                          if (a.subscriptionType < b.subscriptionType) return -1;
                          if (a.subscriptionType > b.subscriptionType) return 1;
                          return 0;
                        })
                        .map((item, rowIndex) => {
                          const row = [
                            item.subscriptionType,
                            item.subscriptionCount
                          ];
                          return (
                            <TableRow
                              key={"row-" + rowIndex}>
                              {row.map((cell, cellIndex) =>
                                <TableCell
                                  data-test={"cell-" + cellIndex}
                                  key={cellIndex}
                                  className={classes.tableCell}>
                                  <Typography className={classes.tableCellText}>{cell}</Typography>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        }
                        )}
                    </TableBody>
                  </Table>
                </TitledPanel>
              </Grid>
              <Grid item xs={12}>
                <TitledPanel dataTest="subscription-info" fontSize="large" title="Subscription Detail">
                  <Table>
                    <TableHead style={{ backgroundColor: '#e8e8e8' }}>
                      <TableRow>
                        {
                          ["Alerts", "Username", "Subscription Name", "ID", "Version", "Type", <span>Max<br />Users</span>, "Expires", "Bucket", "Triples", <span>Full<br />Stats</span>, "Util (%)", "Options"].map((col, index) =>
                            <TableCell key={index} component="th" className={classes.tableHeading}>{col}</TableCell>
                          )
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ backgroundColor: 'white' }}>
                      {subscriptionTripleStatistics
                        .sort(function (a, b) {
                          return a.username.toLowerCase() < b.username.toLowerCase() ?
                            -1
                            :
                            a.username.toLowerCase() > b.username.toLowerCase() ?
                              1
                              :
                              0;
                        })
                        .map((item, rowIndex) => {
                          const maxTriplesAllowed = item.maxTriples ?
                            item.maxTriples
                            :
                            subscriptionOptions.find(option => option.subscriptionType === item.subscriptionType).maxTriples;
                          const utilisation = Math.floor(item.tripleCount / maxTriplesAllowed * 100);
                          const tc = item.tripleCount !== item.previousTripleCount ?
                            <AccessibleTooltip title={"Previously: " + item.previousTripleCount}>
                              <span style={{ color: '#84BC01' }}>{item.tripleCount}</span>
                            </AccessibleTooltip>
                            :
                            <span>{item.tripleCount}</span>;
                          const ut = <AccessibleTooltip title={Math.floor(maxTriplesAllowed / 1000) + "K"}>
                            <span>{utilisation}</span>
                          </AccessibleTooltip>;
                          const showStats = <AccessibleTooltip title="Show detailed statistics">
                            <IconButton aria-label="Show detailed statistics" data-test="full-stats" size="small" onClick={() => this.detailedView(item.subscription)}>
                              <ShowChartIcon />
                            </IconButton>
                          </AccessibleTooltip>;
                          const row = [
                            item.blocked ?
                              <span style={{ color: utilisation > 100 ? '#fff' : '#DD2C00' }}>Blocked</span>
                              :
                              null,
                            item.username,
                            item.name,
                            width !== "xs" && width !== "sm" ?
                              item.subscription.substring(39)
                              :
                              "",
                            item.hasOwnProperty("subscriptionVersion") ? item.subscriptionVersion : "N/A",
                            item.subscriptionType,
                            item.maxUsers,
                            !item.expirationDate || item.expirationDate === 0 ?
                              item.subscriptionType === "Trial" ?
                                <span
                                  data-test="unexpiring-trial"
                                  style={{
                                    display: 'inline-block',
                                    borderRadius: 4,
                                    padding: '2px 6px',
                                    backgroundColor: 'rgb(0, 0, 255, 0.2)'
                                  }}>Unexpiring Trial</span>
                                :
                                "N/A"
                              :
                              <span
                                data-test="expiring-trial"
                                style={{
                                  display: 'inline-block',
                                  borderRadius: 4,
                                  padding: '2px 6px',
                                  backgroundColor: 'rgb(0, 169, 224, 0.2)'
                                }}>
                                {new Date(item.expirationDate).toLocaleDateString()}
                              </span>,
                            item.bucket,
                            tc,
                            showStats,
                            ut
                          ];
                          let options = [
                            item.blocked ?
                              {
                                label: "Unblock",
                                action: this.unblockSubscription,
                                subscription: item.subscription
                              }
                              :
                              {
                                label: "Block",
                                action: this.blockSubscription,
                                subscription: item.subscription
                              }
                          ];
                          if (user.systemAdmin) {
                            options.push({
                              label: "Update Subscription",
                              action: this.setSubscriptionToUpdate,
                              subscription: item.subscription
                            });
                            options.push({
                              label: "Transfer Subscription",
                              action: this.setSubscriptionToTransfer,
                              subscription: item.subscription
                            });
                          }
                          if (user.systemAdmin && item.blocked) {
                            options.push({
                              label: "Optimise Subscription",
                              action: this.optimiseSubscription,
                              subscription: item.subscription
                            });
                            options.push({
                              label: "Port Subscription",
                              action: this.setSubscriptionToPort,
                              subscription: item.subscription
                            });
                          }
                          if (user.systemAdmin) {
                            // Must be at least one hour away from expiration
                            if (item.expirationDate) {
                              if (item.expirationDate > Date.now() + 3600000) {
                                options.push({
                                  label: "Remove Expiration Date",
                                  action: this.renewSubscription,
                                  subscription: item.subscription
                                });
                              }
                            }
                            else {
                              options.push({
                                label: "Add Expiration Date",
                                action: this.addExpirationDate,
                                subscription: item.subscription
                              });
                            }
                          }
                          return this.getTableRow(row, rowIndex, classes, utilisation > 100 ?
                            '#DD2C00'
                            :
                            utilisation > 80 ?
                              '#CA4F0C' :
                              item.expirationDate && item.expirationDate < currentDate ?
                                item.expirationDate < currentDate - 604800000 ?
                                  '#9E9E9E'
                                  :
                                  '#00A9E0'
                                :
                                null,
                            item.expirationDate && item.expirationDate < currentDate - 604800000 ?
                              []
                              :
                              options,
                            item.subscription,
                            item.name
                          );
                        })}
                    </TableBody>
                  </Table>
                </TitledPanel>
              </Grid>
            </Grid>
          }
          {tabValue === 1 &&
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TitledPanel height="full" dataTest="servers" fontSize="large" title="Servers">
                  <div style={{ display: 'flex' }}>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.subSubHeading}>Server Count</Typography>
                      <Typography data-test="server-count" className={classes.statistic}>{activity.length}</Typography>
                    </div>
                    {user.systemAdmin &&
                      <div style={{ flex: 0 }}>
                        <div style={{ minWidth: 240, textAlign: 'right' }}>
                          <PrimaryButton dataTest="update-diskspace" onClick={this.getDiskspace}>Update Diskspace</PrimaryButton>
                        </div>
                      </div>
                    }
                    {user.systemAdmin &&
                      <div style={{ flex: 0 }}>
                        <div style={{ minWidth: 240, textAlign: 'right' }}>
                          <PrimaryButton dataTest="update-statistics" onClick={this.updateStatistics}>Update Statistics</PrimaryButton>
                        </div>
                      </div>
                    }
                  </div>
                  <Table>
                    <TableHead style={{ backgroundColor: '#e8e8e8' }}>
                      <TableRow>
                        {
                          ["Server", "Last Activity", "Free Disk", "Disk Size"].map((col, index) =>
                            <TableCell key={index} component="th" className={classes.tableHeading}>{col}</TableCell>
                          )
                        }
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ backgroundColor: 'white' }}>
                      {activity.map((item, rowIndex) => {
                        const isMaster = item.server.includes("1-0");
                        const row = [
                          item.server,
                          new Date(item.time).toLocaleString(),
                          item.diskSpace.free ? Math.floor(item.diskSpace.free / 1073741824) + "GB" : "Not available",
                          item.diskSpace.size ? Math.floor(item.diskSpace.size / 1073741824) + "GB" : "Not available"
                        ];
                        return (
                          <TableRow
                            key={"row-" + rowIndex}
                            style={isMaster ? { backgroundColor: '#CA4F0C' } : {}}>
                            {row.map((cell, cellIndex) =>
                              <TableCell
                                data-test={"cell-" + cellIndex}
                                key={cellIndex}
                                style={isMaster ? { color: 'white' } : {}}
                                className={classes.tableCell}>
                                <Typography className={classes.tableCellText}>{cell}</Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }
                      )}
                    </TableBody>
                  </Table>
                </TitledPanel>
              </Grid>
            </Grid>
          }
        </div>
        {subscriptionToUpdate &&
          <UpdateSubscriptionDialog
            subscription={subscriptionTripleStatistics.find(subItem => subItem.subscription === subscriptionToUpdate)}
            onCancel={this.cancelUpdateSubscription}
            onUpdate={this.updateSubscription}
          />
        }
        {subscriptionToTransfer &&
          <TransferSubscriptionDialog
            subscription={subscriptionToTransfer}
            onCancel={this.cancelTransferSubscription}
            onTransfer={this.transferSubscription}
          />
        }
        {subscriptionToPort &&
          <PortSubscriptionDialog
            subscription={subscriptionToPort}
            subscriptions={subscriptionTripleStatistics}
            onCancel={this.cancelPortSubscription}
            onPort={this.portSubscription}
          />
        }
        {detailedStatistics &&
          <DetailedStatisticsDialog
            subscription={detailedStatistics}
            title="Detailed Statistics"
            onCancel={this.cancelDetailedView} />
        }
      </div>
    );
  }
}

SystemStatistics.propTypes = {
  user: PropTypes.object,
  width: PropTypes.string,
  classes: PropTypes.object
};

export default withWidth()(withStyles(styles)(SystemStatistics));